import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { additionalIncomeReportAPI } from '../../services/api';
import { downloadTemplate } from '../../utils/exportUtils';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import SuccessModal from '../Common/SuccessModal';
import ConfirmModal from '../Common/ConfirmModal';
import ErrorModal from '../Common/ErrorModal';
import './AdditionalIncomeReportList.css';

const AdditionalIncomeReportList = () => {
  const { t } = useTranslation();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedIds, setSelectedIds] = useState([]);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [confirmData, setConfirmData] = useState(null);
  const fileInputRef = React.useRef(null);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [summary, setSummary] = useState(null);
  const [loadingSummary, setLoadingSummary] = useState(false);

  useEffect(() => {
    loadReports();
    if (dateFrom || dateTo) {
      loadSummary();
    } else {
      setSummary(null);
    }
  }, [page, dateFrom, dateTo]);

  const loadReports = async () => {
    try {
      setLoading(true);
      const response = await additionalIncomeReportAPI.getAll(searchTerm, page, pageSize, dateFrom || null, dateTo || null);
      setReports(response.data.items || []);
      setTotalPages(response.data.totalPages || 1);
    } catch (err) {
      setErrorMessage(t('additionalIncomeReport.list.messages.loadError'));
      setShowErrorModal(true);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadSummary = async () => {
    if (!dateFrom && !dateTo) {
      setSummary(null);
      return;
    }
    try {
      setLoadingSummary(true);
      const response = await additionalIncomeReportAPI.getSummary(dateFrom || null, dateTo || null);
      setSummary(response.data);
    } catch (err) {
      console.error('Error loading summary:', err);
      setSummary(null);
    } finally {
      setLoadingSummary(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    loadReports();
    loadSummary();
  };

  const handleClearDateFilter = () => {
    setDateFrom('');
    setDateTo('');
    setPage(1);
  };

  const handleDelete = (id) => {
    setConfirmAction(() => async () => {
      try {
        await additionalIncomeReportAPI.delete(id);
        setSuccessMessage(t('additionalIncomeReport.list.messages.deleteSuccess'));
        setShowSuccessModal(true);
        loadReports();
      } catch (err) {
        setErrorMessage(t('additionalIncomeReport.list.messages.deleteError'));
        setShowErrorModal(true);
      }
    });
    setConfirmData({
      title: t('common.confirm'),
      message: t('additionalIncomeReport.list.messages.deleteConfirm'),
      confirmText: t('common.delete'),
      cancelText: t('common.cancel')
    });
    setShowConfirmModal(true);
  };

  const handleBulkDelete = () => {
    if (selectedIds.length === 0) {
      setErrorMessage(t('additionalIncomeReport.list.messages.selectToDelete'));
      setShowErrorModal(true);
      return;
    }

    setConfirmAction(() => async () => {
      try {
        const response = await additionalIncomeReportAPI.bulkDelete(selectedIds);
        setSuccessMessage(response.data.message || t('additionalIncomeReport.list.messages.bulkDeleteSuccess', { count: selectedIds.length }));
        setShowSuccessModal(true);
        setSelectedIds([]);
        loadReports();
      } catch (err) {
        setErrorMessage(err.response?.data?.message || t('additionalIncomeReport.list.messages.bulkDeleteError'));
        setShowErrorModal(true);
      }
    });
    setConfirmData({
      title: t('common.confirm'),
      message: t('additionalIncomeReport.list.messages.bulkDeleteConfirm', { count: selectedIds.length }),
      confirmText: t('common.delete'),
      cancelText: t('common.cancel')
    });
    setShowConfirmModal(true);
  };

  const handleConfirm = async () => {
    setShowConfirmModal(false);
    if (confirmAction) {
      await confirmAction();
    }
    setConfirmAction(null);
    setConfirmData(null);
  };

  const handleCancel = () => {
    setShowConfirmModal(false);
    setConfirmAction(null);
    setConfirmData(null);
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedIds(reports.map(r => r.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectOne = (id) => {
    if (selectedIds.includes(id)) {
      setSelectedIds(selectedIds.filter(i => i !== id));
    } else {
      setSelectedIds([...selectedIds, id]);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showExportMenu && !event.target.closest('.export-menu-container')) {
        setShowExportMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showExportMenu]);

  const handleExport = async (format) => {
    try {
      setShowExportMenu(false);
      let response;
      let filename;
      let contentType;

      switch (format) {
        case 'excel':
          response = await additionalIncomeReportAPI.exportExcel();
          filename = `AdditionalIncomeReports_${new Date().toISOString().slice(0, 10)}.xlsx`;
          contentType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
          break;
        case 'csv':
          response = await additionalIncomeReportAPI.exportCsv();
          filename = `AdditionalIncomeReports_${new Date().toISOString().slice(0, 10)}.csv`;
          contentType = 'text/csv;charset=utf-8';
          break;
        case 'pdf':
          response = await additionalIncomeReportAPI.exportPdf();
          filename = `AdditionalIncomeReports_${new Date().toISOString().slice(0, 10)}.pdf`;
          contentType = 'application/pdf';
          break;
        default:
          return;
      }

      const blob = new Blob([response.data], { type: contentType });
      saveAs(blob, filename);
      setSuccessMessage(t('additionalIncomeReport.list.messages.exportSuccess', { format: format.toUpperCase() }));
      setShowSuccessModal(true);
    } catch (err) {
      console.error('Export error:', err);
      setErrorMessage(t('additionalIncomeReport.list.messages.exportError', { format: format.toUpperCase() }) + ` ${err.response?.data?.message || err.message}`);
      setShowErrorModal(true);
    }
  };

  const handleDownloadTemplate = () => {
    downloadTemplate('AdditionalIncomeReport');
    setSuccessMessage(t('additionalIncomeReport.list.messages.templateDownloadSuccess'));
    setShowSuccessModal(true);
  };

  const handleImport = () => {
    fileInputRef.current?.click();
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      setErrorMessage(t('additionalIncomeReport.list.messages.importError'));
      setShowErrorModal(true);
      return;
    }

    try {
      const fileData = await file.arrayBuffer();
      const workbook = XLSX.read(fileData, { type: 'array' });
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const jsonData = XLSX.utils.sheet_to_json(worksheet);

      const reportsToImport = jsonData.map(row => ({
        reportDate: row.Date || row.ReportDate || row.date || row.reportDate || new Date().toISOString().split('T')[0],
        incomeDescriptionAr: row.IncomeDescriptionAr || row.incomeDescriptionAr || row['Income Description (Arabic)'] || '',
        incomeDescriptionEng: row.IncomeDescriptionEng || row.incomeDescriptionEng || row['Income Description (English)'] || '',
        amount: parseFloat(row.Amount || row.amount || 0),
        recipientNameAr: row.RecipientNameAr || row.recipientNameAr || row['Recipient Name (Arabic)'] || '',
        recipientNameEng: row.RecipientNameEng || row.recipientNameEng || row['Recipient Name (English)'] || '',
        notes: row.Notes || row.notes || '',
        isActive: row.IsActive !== undefined ? (typeof row.IsActive === 'boolean' ? row.IsActive : row.IsActive === 'Yes' || row.IsActive === 'true') : true
      })).filter(r => r.incomeDescriptionAr && r.incomeDescriptionEng && r.amount > 0);

      if (reportsToImport.length === 0) {
        setErrorMessage(t('additionalIncomeReport.list.messages.importNoData'));
        setShowErrorModal(true);
        return;
      }

      let successCount = 0;
      let errorCount = 0;

      for (const report of reportsToImport) {
        try {
          await additionalIncomeReportAPI.create(report);
          successCount++;
        } catch (err) {
          errorCount++;
          console.error('Error importing report:', report, err);
        }
      }

      if (successCount > 0) {
        if (errorCount > 0) {
          setSuccessMessage(t('additionalIncomeReport.list.messages.importPartialSuccess', { success: successCount, error: errorCount }));
        } else {
          setSuccessMessage(t('additionalIncomeReport.list.messages.importSuccess', { count: successCount }));
        }
        setShowSuccessModal(true);
        loadReports();
      } else {
        setErrorMessage(t('additionalIncomeReport.list.messages.importFailed'));
        setShowErrorModal(true);
      }

      e.target.value = '';
    } catch (err) {
      setErrorMessage(t('additionalIncomeReport.list.messages.importProcessError'));
      setShowErrorModal(true);
      e.target.value = '';
    }
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">{t('common.loading')}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="list-view-card">
        <div className="page-actions">
        <div className="page-actions-left">
          <Link to="/additional-income-report/create" className="btn-add">
            + {t('additionalIncomeReport.list.addNew')}
          </Link>
          <div className="filters-row">
            <div className="search-container">
              <input
                type="text"
                className="search-input"
                placeholder={t('additionalIncomeReport.list.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch(e)}
              />
              <button className="search-icon" onClick={handleSearch}>
                <i className="fas fa-search"></i>
              </button>
            </div>
            <div className="date-filter-container">
              <div className="date-filter-group">
                <label className="date-filter-label">
                  <i className="fas fa-calendar-alt"></i> {t('additionalIncomeReport.list.dateFrom')}
                </label>
                <input
                  type="date"
                  className="date-filter-input"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                />
              </div>
              <div className="date-filter-group">
                <label className="date-filter-label">
                  <i className="fas fa-calendar-alt"></i> {t('additionalIncomeReport.list.dateTo')}
                </label>
                <input
                  type="date"
                  className="date-filter-input"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                />
              </div>
              {(dateFrom || dateTo) && (
                <button className="btn-clear-filter" onClick={handleClearDateFilter} title={t('common.clear')}>
                  <i className="fas fa-times"></i>
                </button>
              )}
            </div>
          </div>
        </div>
        <div className="action-buttons">
          <div className="export-menu-container">
            <button className="action-btn action-btn-export" title={t('common.export')} onClick={() => setShowExportMenu(!showExportMenu)}>
              <i className="fas fa-file-export"></i>
            </button>
            {showExportMenu && (
              <div className="export-menu">
                <button onClick={() => handleExport('excel')}>
                  <i className="fas fa-file-excel"></i> {t('common.exportExcel')}
                </button>
                <button onClick={() => handleExport('csv')}>
                  <i className="fas fa-file-csv"></i> {t('common.exportCsv')}
                </button>
                <button onClick={() => handleExport('pdf')}>
                  <i className="fas fa-file-pdf"></i> {t('common.exportPdf')}
                </button>
              </div>
            )}
          </div>
          <button className="action-btn action-btn-import" title={t('additionalIncomeReport.list.import')} onClick={handleImport}>
            <i className="fas fa-file-import"></i>
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls"
            style={{ display: 'none' }}
            onChange={handleFileUpload}
          />
          <button className="action-btn action-btn-download" title={t('additionalIncomeReport.list.downloadTemplate')} onClick={handleDownloadTemplate}>
            <i className="fas fa-download"></i>
          </button>
          <button className="action-btn action-btn-delete" title={t('additionalIncomeReport.list.bulkDelete')} onClick={handleBulkDelete} disabled={selectedIds.length === 0}>
            <i className="fas fa-trash"></i>
          </button>
        </div>
      </div>

      <ErrorModal
        show={showErrorModal}
        message={errorMessage}
        onClose={() => setShowErrorModal(false)}
      />

      <SuccessModal
        show={showSuccessModal}
        message={successMessage}
        onClose={() => setShowSuccessModal(false)}
      />

      <ConfirmModal
        show={showConfirmModal}
        title={confirmData?.title}
        message={confirmData?.message}
        confirmText={confirmData?.confirmText}
        cancelText={confirmData?.cancelText}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />

      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>
                <input
                  type="checkbox"
                  onChange={handleSelectAll}
                  checked={selectedIds.length === reports.length && reports.length > 0}
                />
              </th>
              <th>{t('additionalIncomeReport.list.columns.id')}</th>
              <th>{t('additionalIncomeReport.list.columns.date')}</th>
              <th>{t('additionalIncomeReport.list.columns.incomeDescriptionAr')}</th>
              <th>{t('additionalIncomeReport.list.columns.incomeDescriptionEng')}</th>
              <th>{t('additionalIncomeReport.list.columns.amount')}</th>
              <th>{t('additionalIncomeReport.list.columns.recipientNameAr')}</th>
              <th>{t('additionalIncomeReport.list.columns.recipientNameEng')}</th>
              <th>{t('additionalIncomeReport.list.columns.notes')}</th>
              <th>{t('additionalIncomeReport.list.columns.active')}</th>
              <th>{t('additionalIncomeReport.list.columns.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {reports.length === 0 ? (
              <tr>
                <td colSpan="11" className="text-center py-4">
                  {t('additionalIncomeReport.list.messages.noData')}
                </td>
              </tr>
            ) : (
              reports.map((report) => (
                <tr key={report.id}>
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(report.id)}
                      onChange={() => handleSelectOne(report.id)}
                    />
                  </td>
                  <td>{report.id}</td>
                  <td>{new Date(report.reportDate).toLocaleDateString()}</td>
                  <td>{report.incomeDescriptionAr}</td>
                  <td>{report.incomeDescriptionEng}</td>
                  <td>{parseFloat(report.amount).toFixed(2)}</td>
                  <td>{report.recipientNameAr}</td>
                  <td>{report.recipientNameEng}</td>
                  <td>{report.notes ? (report.notes.length > 30 ? report.notes.substring(0, 30) + '...' : report.notes) : '-'}</td>
                  <td>
                    <span className={report.isActive ? 'status-yes-text' : 'status-no-text'}>
                      {report.isActive ? t('common.yes') : t('common.no')}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons-cell">
                      <Link
                        to={`/additional-income-report/view/${report.id}`}
                        className="btn btn-sm btn-view"
                        title={t('common.view')}
                      >
                        <i className="fas fa-eye"></i>
                      </Link>
                      <Link
                        to={`/additional-income-report/edit/${report.id}`}
                        className="btn btn-sm btn-edit"
                        title={t('common.edit')}
                      >
                        <i className="fas fa-edit"></i>
                      </Link>
                      <button
                        className="btn btn-sm btn-delete"
                        onClick={() => handleDelete(report.id)}
                        title={t('common.delete')}
                      >
                        <i className="fas fa-trash"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

        {totalPages > 1 && (
          <div className="pagination-container">
            <ul className="pagination">
              <li className={`page-item ${page === 1 ? 'disabled' : ''}`}>
                <button className="page-link" onClick={() => setPage(page - 1)} disabled={page === 1}>
                  {t('common.previous')}
                </button>
              </li>
              {[...Array(totalPages)].map((_, i) => (
                <li key={i + 1} className={`page-item ${page === i + 1 ? 'active' : ''}`}>
                  <button className="page-link" onClick={() => setPage(i + 1)}>
                    {i + 1}
                  </button>
                </li>
              ))}
              <li className={`page-item ${page === totalPages ? 'disabled' : ''}`}>
                <button className="page-link" onClick={() => setPage(page + 1)} disabled={page === totalPages}>
                  {t('common.next')}
                </button>
              </li>
            </ul>
          </div>
        )}

        {/* Summary/Report Container */}
        {(dateFrom || dateTo) && (
          <div className="summary-container">
            <div className="summary-header">
              <h3 className="summary-title">
                <i className="fas fa-chart-bar"></i> {t('additionalIncomeReport.list.summary.title')}
              </h3>
              {summary && (
                <div className="summary-period">
                  {summary.dateFrom && summary.dateTo && (
                    <span>
                      {new Date(summary.dateFrom).toLocaleDateString()} - {new Date(summary.dateTo).toLocaleDateString()}
                    </span>
                  )}
                </div>
              )}
            </div>
            
            {loadingSummary ? (
              <div className="summary-loading">
                <i className="fas fa-spinner fa-spin"></i> {t('common.loading')}
              </div>
            ) : summary ? (
              <div className="summary-content">
                {/* Key Metrics */}
                <div className="summary-metrics">
                  <div className="summary-metric-card">
                    <div className="metric-icon" style={{ backgroundColor: 'rgba(40, 167, 69, 0.1)', color: '#28a745' }}>
                      <i className="fas fa-list"></i>
                    </div>
                    <div className="metric-content">
                      <div className="metric-label">{t('additionalIncomeReport.list.summary.totalRecords')}</div>
                      <div className="metric-value">{summary.totalCount || 0}</div>
                    </div>
                  </div>
                  
                  <div className="summary-metric-card">
                    <div className="metric-icon" style={{ backgroundColor: 'rgba(191, 79, 31, 0.1)', color: '#bf4f1f' }}>
                      <i className="fas fa-dollar-sign"></i>
                    </div>
                    <div className="metric-content">
                      <div className="metric-label">{t('additionalIncomeReport.list.summary.totalAmount')}</div>
                      <div className="metric-value">{summary.totalAmount?.toFixed(2) || '0.00'} SAR</div>
                    </div>
                  </div>
                  
                  <div className="summary-metric-card">
                    <div className="metric-icon" style={{ backgroundColor: 'rgba(0, 123, 255, 0.1)', color: '#007bff' }}>
                      <i className="fas fa-calculator"></i>
                    </div>
                    <div className="metric-content">
                      <div className="metric-label">{t('additionalIncomeReport.list.summary.averageAmount')}</div>
                      <div className="metric-value">{summary.averageAmount?.toFixed(2) || '0.00'} SAR</div>
                    </div>
                  </div>
                  
                  <div className="summary-metric-card">
                    <div className="metric-icon" style={{ backgroundColor: 'rgba(40, 167, 69, 0.1)', color: '#28a745' }}>
                      <i className="fas fa-check-circle"></i>
                    </div>
                    <div className="metric-content">
                      <div className="metric-label">{t('additionalIncomeReport.list.summary.activeRecords')}</div>
                      <div className="metric-value">{summary.activeCount || 0}</div>
                    </div>
                  </div>
                  
                  <div className="summary-metric-card">
                    <div className="metric-icon" style={{ backgroundColor: 'rgba(220, 53, 69, 0.1)', color: '#dc3545' }}>
                      <i className="fas fa-times-circle"></i>
                    </div>
                    <div className="metric-content">
                      <div className="metric-label">{t('additionalIncomeReport.list.summary.inactiveRecords')}</div>
                      <div className="metric-value">{summary.inactiveCount || 0}</div>
                    </div>
                  </div>
                </div>

                {/* Monthly Breakdown */}
                {summary.byMonth && summary.byMonth.length > 0 && (
                  <div className="summary-section">
                    <h4 className="section-title">
                      <i className="fas fa-calendar-alt"></i> {t('additionalIncomeReport.list.summary.monthlyBreakdown')}
                    </h4>
                    <div className="monthly-list">
                      {summary.byMonth.map((month) => (
                        <div key={`${month.year}-${month.month}`} className="month-item">
                          <div className="month-name">{month.monthName}</div>
                          <div className="month-stats">
                            <span className="month-count">{month.count} {t('additionalIncomeReport.list.summary.records')}</span>
                            <span className="month-income">{month.totalAmount?.toFixed(2) || '0.00'} SAR</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Top Recipients */}
                {summary.topRecipients && summary.topRecipients.length > 0 && (
                  <div className="summary-section">
                    <h4 className="section-title">
                      <i className="fas fa-user-tie"></i> {t('additionalIncomeReport.list.summary.topRecipients')}
                    </h4>
                    <div className="properties-list">
                      {summary.topRecipients.map((recipient, index) => (
                        <div key={index} className="property-item">
                          <div className="property-rank">#{index + 1}</div>
                          <div className="property-info">
                            <div className="property-name">{recipient.recipientName}</div>
                            <div className="property-stats">
                              <span className="property-count">{recipient.count} {t('additionalIncomeReport.list.summary.records')}</span>
                            </div>
                          </div>
                          <div className="property-income">
                            {recipient.totalAmount?.toFixed(2) || '0.00'} SAR
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="summary-empty">
                <i className="fas fa-info-circle"></i> {t('additionalIncomeReport.list.summary.noData')}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdditionalIncomeReportList;




